<template>
  <div class="opc-card-cascade">
    <div class="opc-card-cascade__item">
      <div id="group0-title" class="opc-card-cascade__title">
        <slot :name="group0.slot">
          {{ group0.title }}
        </slot>
      </div>
      <zm-radio-group
        class="opc-card-radio opc-card-cascade__group0-radios"
        aria-labelledby="group0-title"
        name="group0"
        v-model="value[0]"
        @input="handleGroup0Change"
      >
        <zm-tooltip v-for="(item, index) in group0.options" :key="index" :disabled="!(typeof item.disabled === 'string')">
          <div slot="content" v-html="item.disabled || ''" />
        <zm-radio :label="item.value" :disabled="!!item.disabled">
          <p class="opc-card-cascade__group0-title first-capitalize">
            {{ item.title }}
            <save-count :monthly-price='computeOneMonthlyPrice(shortestCycleOption)'
                        :annual-price='computeOneMonthlyPrice(item) * 12' :disabled='!!item.disabled' />
          </p>
          <p class="opc-card-cascade__group0-subtitle">
            <span class="opc-card-cascade__group0-subtitle-price">
              {{ item.subTitle }}
            </span>
            {{ item.description }}<priceVATTip />
          </p>
        </zm-radio>
        </zm-tooltip>
      </zm-radio-group>
      <zm-alert
        v-if="monthlyDisabledAlertMessage"
        class="opc-common-info-alert"
        type="warning"
        :closable="false"
        style="margin: 14px 0px 0px;"
        show-icon>
        <p v-html="monthlyDisabledAlertMessage" />
      </zm-alert>
    </div>
    <div v-if="divideLine" class="divide-line"></div>
    <div class="opc-card-cascade__item" v-if="group1.options.length">
      <div class="opc-card-cascade__title">
        <slot :name="group1.slot">
          <span id="group1-title">{{ group1.title }}</span>
        </slot>
      </div>
      <zm-radio-group
        class="opc-card-radio opc-card-cascade__group1-radios"
        aria-labelledby="group1-title"
        name="group1"
        v-model="value[1]"
        @input="handleGroup1Change"
      >
        <zm-radio v-for="item in group1.options" :key="item.value" :label="item.value" :aria-label="group1.ariaLabel">
          <p class="opc-card-cascade__group1-title">{{ item.title }}</p>
          <p class="opc-card-cascade__group1-subtitle">
            <span class="opc-card-cascade__group1-subtitle-desc">{{ item.subTitle }}</span>
            {{ item.description }}<priceVATTip />
          </p>
        </zm-radio>
      </zm-radio-group>
    </div>
  </div>
</template>

<script>
import SaveCount from '@/components/save-count.vue';
import _ from 'lodash';
import { BillingCycleEnum } from '@/common/constants';

export default {
  name: 'OpcCardCascade',
  components: { SaveCount },
  data: () => {
    return {};
  },
  props: {
    monthlyDisabledAlertMessage: String,
    trackingData: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => {},
    },
    divideLine: Boolean,
  },
  computed: {
    group0() {
      return {
        ...this.config.group0,
        options: this.config.options,
      };
    },
    group1() {
      return this.getGroup1(this.value[0]);
    },
    shortestCycleOption() {
      return _.sortBy(this.group0.options, o => BillingCycleEnum[o.value])[0];
    },
  },
  methods: {
    handleGroup0Change(v) {
      const group1Opts = this.getGroup1(v).options
      if (!group1Opts.find((item) => item.value === this.value[1])) {
        const group1Value = group1Opts.at(-1).value;
        this.$emit('input', [v, group1Value]);
        this.$emit('change', [v, group1Value]);
      } else {
        this.$emit('input', [v, this.value[1]]);
        this.$emit('change', [v, this.value[1]]);
      }
      const { buyInteraction, getBuySelection } = this.trackingData[0] || {}
      this.$tracking.buyInteraction(buyInteraction, (getBuySelection && getBuySelection(v)) || v || '')
    },
    handleGroup1Change(v) {
      this.$emit('input', [this.value[0], v]);
      this.$emit('change', [this.value[0], v]);

      const { buyInteraction, getBuySelection } = this.trackingData[1] || {}
      this.$tracking.buyInteraction(buyInteraction, (getBuySelection && getBuySelection(this.value[0], v)) || v || '')
    },
    computeOneMonthlyPrice(option) {
      return option[option.value] / BillingCycleEnum[option.value];
    },
    getGroup1(v) {
      const group1Options = this.config.options.find((item) => item.value === v)?.options ?? [];
      return {
        ...this.config.group1,
        options: group1Options,
      };
    }
  }
};
</script>

<style lang="scss">
.opc-card-cascade {
  .opc-card-cascade__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 24px;

    .opc-card-radio {
      width: 100%;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      &.opc-card-cascade__group0-radios {
        .zm-radio__label--inner {
          padding: 16px;
        }
      }
      &.opc-card-cascade__group1-radios {
        .zm-radio__label--inner {
          padding: 16px;
        }
      }
      .zm-radio {
        flex: 1;
        .zm-radio__wrap {
          width: 100%;
        }
        .zm-radio__label--inner {
          width: 100%;
        }
      }
    }
  }

  .is-disabled {
    .opc-card-cascade__group0-title,
    .opc-card-cascade__group0-subtitle,
    .opc-card-cascade__group0-subtitle-price,
    .opc-card-cascade__group1-title,
    .opc-card-cascade__group1-subtitle,
    .opc-card-cascade__group1-subtitle-price {
      color: #9191A4;
    }
  }

  &__group0-title {
    color: #00053D;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
  }
  &__group0-subtitle {
    font-family: 'Almaden Sans';
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0em;
    color: #515079;
    &-price {
      color: #00053D;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 33px;
    }
    &-desc {
      font-family: 'Almaden Sans';
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
      letter-spacing: 0em;
      text-align: center;
      color: #00031f;
    }
  }
  &__group1-title {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 150%;
    color: #00031f;
  }
  &__group1-subtitle {
    font-family: 'Almaden Sans';
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0em;
    color: #666487;
    text-align: left;
    &-desc {
      font-family: 'Almaden Sans';
      font-size: 22px;
      font-weight: 700;
      line-height: 33px;
      letter-spacing: 0em;
      text-align: left;
      color: #00031f;
    }
  }

  .opc-card-cascade__title {
    display: flex;
    align-items: center;
    color: #00031f;
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 150%;
    margin-bottom: 8px;
  }

  .divide-line {
    width: 100%;
    border-top: 1px solid #dfdfe4;
    margin: 24px 0;
  }
}
</style>
